import { createContext, ReactNode, useContext } from 'react'
import { CreateHandbookItemParams, HandbookItemParams } from 'api/clients/HandbookClient'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ArticleView, HandbookItem, HandbookItemType, HandbookV2 } from 'types'

import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ARTICLE_VIEW_PARAM } from 'components/ArticleViewSelect/types'
import { useHistory, useLocation } from 'react-router-dom'
import { useFinderRefresh } from 'screens/Finder/hooks/useFinderRefresh'
import { useHandbookRouteMatch } from '../hooks/useHandbookRouteMatch'
import { handbookItemLink, stringType } from '../util'
import { useRealtime } from './hooks/useRealtime'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { observer } from 'mobx-react-lite'
import { HandbookTreeStore } from '../Stores/HandbookTreeStore'
import { useRootStore } from 'stores/RootStore'
import QueryString from 'qs'

interface HandbookItemContextProps {
  currentHandbook?: HandbookV2
  treeStore?: HandbookTreeStore
  createItem: (values: CreateHandbookItemParams) => Promise<HandbookItem | undefined>
  updateItem: (item: HandbookItem, values: HandbookItemParams) => Promise<HandbookItem | undefined>
}

interface Props {
  handbookKey?: string
  children: ReactNode
}

const HandbookItemsContext = createContext<HandbookItemContextProps>({
  currentHandbook: undefined,
  treeStore: undefined,
  createItem: Promise.resolve,
  updateItem: Promise.resolve,
})

const HandbookItemProvider = observer(({ handbookKey, children }: Props) => {
  const handbooksStore = useRootStore().handbooks
  const treeStore = handbooksStore.get(handbookKey)
  const currentHandbook = treeStore?.handbook
  const history = useHistory()
  const { docKey: inDoc } = useHandbookRouteMatch()
  const refreshFinder = useFinderRefresh()
  const { currentFolder, reload: reloadCurrentFolder } = useCurrentFolderStore()
  const location = useLocation()

  useRealtime({
    currentHandbook,
    onUpdate: () => handbooksStore.refresh(currentHandbook?.key || ''),
  })

  const updateItem: HandbookItemContextProps['updateItem'] = async (item, values) => {
    if (!treeStore) {
      return
    }
    // Update
    const dismissLoading = showToastMessage(
      `Updating ${stringType(values.type || item.type)}...`,
      'loading',
      undefined,
      0,
    )
    try {
      const updatedItem = await treeStore.updateItem({ id: item.id, parent_id: item.parent_id }, values)
      dismissLoading()
      if (item.folder?.key === currentFolder?.id) {
        reloadCurrentFolder()
      }
      if (!inDoc) {
        refreshFinder()
      }
      return updatedItem
    } catch (e) {
      dismissLoading()
      showErrorMessageToast('There was a problem saving this item, please try again')
    }
  }

  const createItem: HandbookItemContextProps['createItem'] = async (values) => {
    if (!treeStore) {
      return
    }
    const dismissLoading = showToastMessage(
      `${values.key ? 'Adding' : 'Creating'} ${stringType(values.type)}...`,
      'loading',
      undefined,
      0,
    )
    try {
      const newItem = await treeStore.createItem(values)
      dismissLoading()
      if ([HandbookItemType.Document, HandbookItemType.NestedHandbook].includes(newItem.type)) {
        let newParams = {}
        if (newItem.document) {
          newParams = {
            [ARTICLE_VIEW_PARAM]: ArticleView.Editing,
          }
        }
        // this setTimeout is necessary to prevent the navigation from happening before the React has finished updating the handbook tree
        setTimeout(() => {
          history.push({
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            pathname: handbookItemLink(currentHandbook!, newItem),
            search: QueryString.stringify(
              {
                ...QueryString.parse(location.search, { ignoreQueryPrefix: true }),
                ...newParams,
              },
              { filter: (_, value) => value ?? undefined },
            ),
          })
        }, 5)
      }
      return newItem
    } catch (e) {
      dismissLoading()
      showErrorMessageToast('There was a problem saving this item, please try again')
    }
  }

  return (
    <HandbookItemsContext.Provider
      value={{
        currentHandbook,
        treeStore,
        createItem,
        updateItem,
      }}
    >
      {children}
    </HandbookItemsContext.Provider>
  )
})

export default HandbookItemProvider

export const useHandbookItems = () => useContext(HandbookItemsContext)
